import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  getClubTeams,
  getClubCoaches,
  getClubAthletes,
  getClubSessions,
  getClubInvoices,
  type ClubCoach,
  type ClubAthlete,
  type ClubSession,
  type ClubInvoice,
} from "../../services/api";
import {
  createInvitation,
  getSentInvitations,
  cancelInvitation,
  type Invitation,
} from "../../services/invitations";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { SectionCard, StatCard, StatGrid } from "../../components/admin/AdminCard";
import MiniAreaChart from "../../components/admin/MiniAreaChart";
import toast from "react-hot-toast";
import PendingInvitations from "../../components/club/PendingInvitations";
import ClubOnboarding from "../../components/club/ClubOnboarding";
import JoinRequests from "../../components/club/JoinRequests";
import { useTour } from "../../components/tour/TourProvider";
import ClubCollectionsCard from "../../components/club/ClubCollectionsCard";

type Team = { id: string; name: string; age_group?: string | null; sport_type?: string | null };

export default function ClubOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClubId, activeRole, token, loading: authLoading } = useAuth();
  const tour = useTour();

  /**
   * Take a first-time club admin through the tour rather than waiting to be
   * asked. Runs once ever: `seen` is set on any exit, including an early one,
   * so nobody is handed it twice.
   *
   * Deliberately only here, on the club's landing page — auto-starting from a
   * deep link would yank someone away from whatever they came to do.
   */
  useEffect(() => {
    // Only for a session that is actually signed in and settled. While /auth/me
    // is still in flight the role is not known yet, and starting on a guess
    // would open the tour for whoever happens to be at the keyboard.
    if (authLoading || !token) return;
    if (activeRole !== "club_admin") return;
    if (tour.seen || tour.active) return;
    const id = setTimeout(() => tour.start(), 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, token, activeRole, tour.seen, tour.active]);

  const [teams, setTeams] = useState<Team[]>([]);
  const [coaches, setCoaches] = useState<ClubCoach[]>([]);
  const [athletes, setAthletes] = useState<ClubAthlete[]>([]);
  const [sessions, setSessions] = useState<ClubSession[]>([]);
  const [invoices, setInvoices] = useState<ClubInvoice[]>([]);
  const [loading, setLoading] = useState(true);

  // Invitation states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteReloadKey, setInviteReloadKey] = useState(0);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => {
    if (!activeClubId) return;

    (async () => {
      setLoading(true);
      try {
        const [teamsData, coachesData, athletesData, sessionsData, invitationsData, invoicesData] = await Promise.all([
          getClubTeams(activeClubId),
          getClubCoaches(activeClubId),
          getClubAthletes(activeClubId),
          getClubSessions(activeClubId),
          getSentInvitations(),
          // Invoices power the "De încasat" tile; a club without billing set
          // up just yields an empty list, so a failure here shouldn't blank
          // out the whole dashboard.
          getClubInvoices(activeClubId).catch(() => [] as ClubInvoice[]),
        ]);
        setTeams(teamsData);
        setCoaches(coachesData);
        setAthletes(athletesData);
        setSessions(sessionsData);
        setInvitations(invitationsData.filter(inv => inv.club_id === activeClubId && inv.status === "pending"));
        setInvoices(invoicesData);
      } catch (error) {
        console.error("Failed to load overview data:", error);
      }
      setLoading(false);
    })();
  }, [activeClubId]);

  const handleInviteCoach = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeClubId || !inviteEmail) return;

    setInviteLoading(true);
    try {
      await createInvitation({
        email: inviteEmail.trim(),
        role_code: "coach",
        club_id: activeClubId,
      });
      toast.success(t("invite.sent", "Invitație trimisă cu succes!"));
      setInviteEmail("");
      setShowInviteModal(false);
      // The pending list is its own component now; nudge it to reload.
      setInviteReloadKey((k) => k + 1);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t("invite.error", "Nu s-a putut trimite invitația"));
    }
    setInviteLoading(false);
  };

  if (loading) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
      </ClubAdminLayout>
    );
  }

  // ---- Derived dashboard metrics (all from data already loaded) ----------
  const now = new Date();
  const rosterOf = (teamId?: string | null) =>
    teamId ? athletes.filter((a) => a.team?.id === teamId).length : 0;

  // Athletes that joined in the current calendar month / last 7 days.
  const joinedThisMonth = athletes.filter((a) => {
    if (!a.joined_at) return false;
    const d = new Date(a.joined_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const newAthletes = athletes
    .filter((a) => {
      if (!a.joined_at) return false;
      return now.getTime() - new Date(a.joined_at).getTime() <= 7 * 24 * 3600 * 1000;
    })
    .sort((a, b) => new Date(b.joined_at!).getTime() - new Date(a.joined_at!).getTime())
    .slice(0, 6);

  // Average attendance across athletes (present / total over all recorded
  // attendance rows). Falls back to null when nothing is recorded yet.
  const attTotals = athletes.reduce(
    (acc, a) => {
      acc.present += a.stats?.attendance?.present ?? 0;
      acc.total += a.stats?.attendance?.total ?? 0;
      return acc;
    },
    { present: 0, total: 0 }
  );
  const avgAttendance = attTotals.total > 0 ? Math.round((attTotals.present / attTotals.total) * 100) : null;

  // Weekly attendance rate for the last 8 weeks, from past sessions'
  // present_count vs. the current team roster.
  const WEEKS = 8;
  const startOfWeek = (d: Date) => {
    const x = new Date(d);
    const day = x.getDay();
    x.setDate(x.getDate() - (day === 0 ? 6 : day - 1));
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const curWeekStart = startOfWeek(now);
  const weekBuckets = Array.from({ length: WEEKS }, (_, i) => {
    const start = new Date(curWeekStart);
    start.setDate(start.getDate() - (WEEKS - 1 - i) * 7);
    return { start, present: 0, roster: 0 };
  });
  sessions.forEach((s) => {
    const end = new Date(s.end_at);
    if (end >= now) return; // only sessions that already happened
    const roster = rosterOf(s.team?.id);
    if (roster <= 0) return;
    const ws = startOfWeek(new Date(s.start_at)).getTime();
    const idx = weekBuckets.findIndex((b) => b.start.getTime() === ws);
    if (idx === -1) return;
    weekBuckets[idx].present += s.present_count ?? 0;
    weekBuckets[idx].roster += roster;
  });
  const attendanceSeries = weekBuckets.map((b) =>
    b.roster > 0 ? Math.min(100, Math.round((b.present / b.roster) * 100)) : 0
  );
  const attendanceLabels = weekBuckets.map((b) =>
    b.start.toLocaleDateString("ro-RO", { day: "numeric", month: "short" })
  );
  const hasAttendanceData = weekBuckets.some((b) => b.roster > 0);

  // Upcoming sessions (next to happen), soonest first.
  const upcomingSessions = sessions
    .filter((s) => new Date(s.start_at).getTime() >= now.getTime())
    .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())
    .slice(0, 5);

  // Billing snapshot for the "De încasat" tile.
  const centsBy = (pred: (i: ClubInvoice) => boolean) =>
    invoices.filter(pred).reduce((sum, i) => sum + (i.amount_cents || 0), 0);
  const dueCents = centsBy((i) => i.status === "open" || i.status === "draft");
  const overdueCount = invoices.filter((i) => i.status === "uncollectible").length;
  const eur = (c: number) =>
    `${(c / 100).toLocaleString("ro-RO", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
        <AdminPageHeader
          title={t("admin.overview", "Prezentare generală")}
          subtitle={t("admin.overviewSubtitle", "Rezumatul clubului tău — sportivi, prezență, sesiuni și încasări, dintr-o privire.")}
          icon={<ClubNavIcon id="overview" className="w-6 h-6" />}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <AdminButton
                variant="secondary"
                onClick={() => navigate("/dashboard/admin-club/teams")}
                icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>}
              >
                {t("admin.createTeam", "Creează echipă")}
              </AdminButton>
              <AdminButton
                onClick={() => setShowInviteModal(true)}
                icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>}
              >
                {t("invite.inviteCoach", "Invită antrenor")}
              </AdminButton>
              <AdminButton
                variant="secondary"
                onClick={() => navigate("/dashboard/admin-club/athletes")}
                icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>}
              >
                {t("invite.inviteAthlete", "Invită sportiv")}
              </AdminButton>
            </div>
          }
        />

        {/* First-run checklist. Removes itself once the club is set up, so a
            long-running club never sees it. */}
        {/* Shows itself only when somebody is waiting. */}
        <JoinRequests clubId={activeClubId} />

        <ClubOnboarding clubId={activeClubId} />

        {/* Stats — clickable summary tiles routing to each list view. */}
        <StatGrid>
          {[
            {
              label: t("admin.activeAthletes", "Sportivi activi"),
              value: athletes.length,
              dot: "bg-emerald-500",
              to: "/dashboard/admin-club/athletes",
              hint: joinedThisMonth > 0 ? `▲ ${joinedThisMonth} ${t("admin.thisMonth", "luna asta")}` : t("admin.noNewThisMonth", "Niciun sportiv nou luna asta"),
              hintTone: (joinedThisMonth > 0 ? "up" : "muted") as "up" | "down" | "muted",
            },
            {
              label: t("admin.coaches", "Antrenori"),
              value: coaches.length,
              dot: "bg-violet-500",
              to: "/dashboard/admin-club/coaches",
              hint: `${teams.length} ${t("admin.teams", "Echipe").toLowerCase()}`,
              hintTone: "muted" as const,
            },
            {
              label: t("admin.avgAttendance", "Prezență medie"),
              value: avgAttendance !== null ? `${avgAttendance}%` : "—",
              dot: "bg-sky-500",
              to: "/dashboard/admin-club/sessions",
              hint: avgAttendance !== null ? t("admin.fromRecorded", "Din prezențele înregistrate") : t("admin.noAttendanceYet", "Fără prezențe încă"),
              hintTone: "muted" as const,
            },
            {
              label: t("admin.toCollect", "De încasat"),
              value: eur(dueCents),
              dot: "bg-amber-500",
              // What the club collects FROM athletes — not its own platform
              // invoices, which is where this used to lead.
              to: "/dashboard/admin-club/receivables",
              valueClass: dueCents > 0 ? "text-slate-800" : "text-slate-800",
              hint: overdueCount > 0 ? `▼ ${overdueCount} ${t("admin.overdueShort", "restanțe")}` : t("admin.noOverdue", "Fără restanțe"),
              hintTone: (overdueCount > 0 ? "down" : "muted") as "up" | "down" | "muted",
            },
          ].map((s) => (
            <button
              key={s.to}
              onClick={() => navigate(s.to)}
              className="text-left rounded-2xl transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <StatCard label={s.label} value={s.value} dotClass={s.dot} hint={s.hint} hintTone={s.hintTone} valueClass={(s as any).valueClass} />
            </button>
          ))}
        </StatGrid>

        {/* The money, above attendance: a club admin opening the dashboard is
            usually there to find out who has not paid. */}
        <ClubCollectionsCard clubId={activeClubId} />

        {/* Attendance trend + upcoming sessions */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SectionCard
            title={t("admin.attendance", "Prezență")}
            subtitle={t("admin.last8Weeks", "ultimele 8 săptămâni")}
            actions={
              <button onClick={() => navigate("/dashboard/admin-club/sessions")} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                {t("common.details", "Detalii")}
              </button>
            }
            bodyPadded
          >
            {hasAttendanceData ? (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-2xl font-extrabold text-slate-800 tabular-nums">
                    {avgAttendance !== null ? `${avgAttendance}%` : `${attendanceSeries[attendanceSeries.length - 1]}%`}
                  </span>
                  <span className="text-xs font-medium text-slate-500">{t("admin.avgLabel", "medie")}</span>
                </div>
                <MiniAreaChart
                  data={attendanceSeries}
                  labels={attendanceLabels}
                  color="#6366f1"
                  max={100}
                  height={170}
                  formatValue={(v) => `${v}%`}
                  className="text-slate-400"
                />
              </>
            ) : (
              <div className="py-12 text-center">
                <p className="text-sm text-slate-400">{t("admin.noAttendanceChart", "Graficul de prezență apare după primele sesiuni cu prezență înregistrată.")}</p>
              </div>
            )}
          </SectionCard>

          <SectionCard
            title={t("admin.upcomingSessions", "Următoarele sesiuni")}
            actions={
              <button onClick={() => navigate("/dashboard/admin-club/calendar")} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                {t("admin.calendar", "Calendar")}
              </button>
            }
            bodyPadded
          >
            <div className="space-y-2.5">
              {upcomingSessions.map((s) => {
                const start = new Date(s.start_at);
                return (
                  <button
                    key={s.id}
                    onClick={() => navigate("/dashboard/admin-club/sessions")}
                    className="flex items-center w-full gap-3 p-3 text-left transition-colors border bg-slate-50 rounded-xl border-slate-100 hover:border-indigo-300"
                  >
                    <div className="flex flex-col items-center justify-center flex-shrink-0 w-12 h-12 bg-white border rounded-xl border-slate-200">
                      <span className="text-[10px] font-semibold uppercase text-slate-400 leading-none">
                        {start.toLocaleDateString("ro-RO", { month: "short" })}
                      </span>
                      <span className="text-lg font-bold leading-tight text-slate-800">{start.getDate()}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate text-slate-800">{s.title}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {start.toLocaleDateString("ro-RO", { weekday: "short" })}{" · "}
                        {start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {s.team?.name ? ` · ${s.team.name}` : ""}
                      </div>
                    </div>
                    <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${s.type === "sport" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                      {s.type === "sport" ? t("admin.sport", "Sport") : t("admin.educational", "Educațional")}
                    </span>
                  </button>
                );
              })}
              {upcomingSessions.length === 0 && (
                <p className="py-10 text-center text-sm text-slate-400">{t("admin.noUpcomingSessions", "Nicio sesiune programată")}</p>
              )}
            </div>
          </SectionCard>
        </div>

        {/* New athletes this week */}
        {newAthletes.length > 0 && (
          <SectionCard
            title={t("admin.newAthletes", "Sportivi noi")}
            subtitle={t("admin.thisWeek", "săptămâna aceasta")}
            actions={
              <button onClick={() => navigate("/dashboard/admin-club/athletes")} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                {t("common.viewAll", "Vezi toate")}
              </button>
            }
            bodyPadded
          >
            <div className="grid gap-2.5 sm:grid-cols-2">
              {newAthletes.map((a) => {
                const name = a.user.full_name || a.user.email;
                return (
                  <div key={a.id} className="flex items-center gap-3 p-3 border bg-slate-50 rounded-xl border-slate-100">
                    <div className="flex items-center justify-center flex-shrink-0 text-sm font-bold rounded-full w-9 h-9 bg-emerald-100 text-emerald-700">
                      {name.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate text-slate-800">{name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {a.team?.name || t("admin.noTeam", "Fără echipă")}
                        {a.joined_at ? ` · ${new Date(a.joined_at).toLocaleDateString("ro-RO", { day: "numeric", month: "short" })}` : ""}
                      </div>
                    </div>
                    <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                      {t("admin.new", "Nou")}
                    </span>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        {/* Pending invitations.
            Was built from `getSentInvitations`, which returns only what the
            signed-in person sent — so a coordinator's invitations, and a
            co-admin's, were invisible here. The shared section asks per CLUB,
            and lets an admin activate an account outright. */}
        <PendingInvitations clubId={activeClubId} reloadKey={inviteReloadKey} />

        {/* Recent Sessions — last 20 sessions that have already happened
            (end_at < now), sorted newest-first. Earlier this section showed
            the freshest rows by start_at which mixed in upcoming sessions
            and excluded the actual past data the admin wants to audit. */}
        {(() => {
          const nowTs = Date.now();
          const pastSessions = sessions
            .filter((s) => new Date(s.end_at).getTime() < nowTs)
            .sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime())
            .slice(0, 20);
          return (
        <SectionCard
          title={t("admin.recentSessions", "Sesiuni desfășurate recent")}
          actions={
            <button onClick={() => navigate("/dashboard/admin-club/sessions")} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
              {t("common.viewAll", "Vezi toate")}
            </button>
          }
          bodyPadded
        >
          <div className="space-y-3">
            {pastSessions.map((session) => {
              const startAt = new Date(session.start_at);
              const endAt = new Date(session.end_at);
              const durationMin = Math.max(0, Math.round((endAt.getTime() - startAt.getTime()) / 60000));
              const durationLabel = durationMin >= 60
                ? `${Math.floor(durationMin / 60)}h ${durationMin % 60 ? `${durationMin % 60}m` : ""}`.trim()
                : `${durationMin}m`;
              const totalInTeam = athletes.filter(a => a.team?.id === session.team?.id).length;
              const presentCount = session.present_count ?? 0;
              const attendancePct = totalInTeam > 0 && presentCount >= 0
                ? Math.round((presentCount / totalInTeam) * 100)
                : null;
              const locName = session.location?.name || session.location_text;
              return (
                <div key={session.id} className="p-4 border bg-slate-50 rounded-xl border-slate-100 hover:border-indigo-300 transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-slate-800 break-words">{session.title}</h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {startAt.toLocaleDateString("ro-RO", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                        {" · "}
                        {startAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {" – "}
                        {endAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {" · "}
                        {durationLabel}
                      </p>
                    </div>
                    <span className={`flex-shrink-0 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${session.type === "sport" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                      {session.type === "sport" ? t("admin.sport", "Sport") : t("admin.educational", "Educațional")}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    {session.team?.name && (
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        {session.team.name}
                      </span>
                    )}
                    {session.team?.sport_type && <span className="text-slate-400">·</span>}
                    {session.team?.sport_type && <span>{session.team.sport_type}</span>}
                    {session.team?.age_group && <span className="text-slate-400">·</span>}
                    {session.team?.age_group && <span>{session.team.age_group}</span>}
                    {session.coach && (
                      <>
                        <span className="text-slate-400">·</span>
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          {session.coach.full_name || session.coach.email}
                        </span>
                      </>
                    )}
                    {locName && (
                      <>
                        <span className="text-slate-400">·</span>
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                          {locName}
                        </span>
                      </>
                    )}
                  </div>
                  {/* Attendance metric — only past sessions have meaningful
                      data here. Show "X / Y prezenți (Z%)" when we can
                      compute the total team roster, otherwise just "X prezenți". */}
                  {endAt < new Date() && (
                    <div className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-2 text-xs">
                      <span className="font-medium text-slate-700">
                        {presentCount}
                        {totalInTeam > 0 ? ` / ${totalInTeam}` : ""}
                        {" "}{t("admin.present", "prezenți")}
                      </span>
                      {attendancePct !== null && (
                        <span className={`px-1.5 py-0.5 rounded font-medium ${
                          attendancePct >= 75 ? "bg-emerald-100 text-emerald-700" :
                          attendancePct >= 50 ? "bg-amber-100 text-amber-700" :
                          "bg-red-100 text-red-700"
                        }`}>
                          {attendancePct}%
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {pastSessions.length === 0 && (
              <p className="py-8 text-center text-slate-400">
                {sessions.length === 0
                  ? t("admin.noSessions", "Nicio sesiune încă")
                  : t("admin.noPastSessions", "Nicio sesiune desfășurată încă")}
              </p>
            )}
          </div>
        </SectionCard>
          );
        })()}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={() => setShowInviteModal(false)}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">{t("invite.inviteCoach", "Invită antrenor")}</h3>
              <button onClick={() => setShowInviteModal(false)} className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleInviteCoach} className="flex flex-col min-h-0 flex-1">
              <div className="p-5 space-y-4 overflow-y-auto">
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">{t("common.email", "Email")}</label>
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="coach@example.com"
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  type="submit"
                  disabled={inviteLoading}
                  className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                >
                  {inviteLoading ? t("common.sending", "Se trimite...") : t("invite.send", "Trimite invitația")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </ClubAdminLayout>
  );
}
